import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { analyzeSkin } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";
import { motion } from "framer-motion";
import { ScanFace, UploadCloud, Loader2, RefreshCw, ArrowRight, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function SkinAnalysis() {
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [consent, setConsent] = useState(false);
  const inputRef = useRef(null);

  const handleFile = (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please upload a JPEG or PNG image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setPreview(reader.result); setBase64(reader.result); setResult(null); };
    reader.readAsDataURL(file);
  };

  const analyze = async () => {
    if (!base64) return;
    if (!consent) { toast.error("Please agree to the privacy notice first."); return; }
    setLoading(true);
    try {
      const res = await analyzeSkin({ image_base64: base64 });
      setResult(res);
      if (res.overall_score === 0) toast.info("Please upload a clearer, well-lit selfie.");
    } catch {
      toast.error("Analysis failed. Please try another photo.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setPreview(null); setBase64(null); setResult(null); };

  if (!user) {
    return (
      <main className="min-h-screen flex items-center justify-center relative z-10 px-6 pt-28 pb-16">
        <div data-testid="analysis-auth-gate" className="text-center max-w-md">
          <Lock className="w-12 h-12 text-[#9A7420] mx-auto mb-6" strokeWidth={1.2} />
          <p className="overline text-[#9A7420] mb-3">Members Only</p>
          <h1 className="font-display text-5xl tracking-tight mb-4">Sign in for your skin scan</h1>
          <p className="text-[#57515F] mb-8">
            Create a free account so your selfie and 12-parameter results are saved to your record — and you can track your progress over time.
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/login" data-testid="gate-login" className="btn-primary">Sign In</Link>
            <Link to="/register" data-testid="gate-register" className="btn-secondary">Create Account</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-28 pb-28 min-h-screen relative z-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-14">
          <p className="overline text-[#B0872A] mb-4">AI Diagnostic</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] mb-5">
            Instant AI skin analysis
          </h1>
          <p className="text-[#57515F] text-lg">
            Upload a clear, well-lit selfie. Our AI evaluates your skin across 12 clinical
            parameters and recommends a personalised path. Cosmetic guidance only — not a medical diagnosis.
          </p>
          <p className="text-[#8B8494] text-sm mt-3 flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-[#B0872A] mt-0.5 flex-shrink-0" strokeWidth={1.6} />
            Your photo is analyzed instantly and <b className="text-[#57515F]">never stored</b> on our servers. Only your non-identifying results are saved to your private account.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <Reveal>
            <div>
              <div
                data-testid="upload-dropzone"
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                className="bg-white/60 backdrop-blur-xl border border-dashed border-[#B0872A]/60 rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-white/80 transition-colors min-h-[420px]"
              >
                {preview ? (
                  <img src={preview} alt="preview" className="max-h-[380px] rounded-xl object-contain" />
                ) : (
                  <div className="text-center">
                    <UploadCloud className="w-14 h-14 text-[#B0872A] mx-auto mb-5" strokeWidth={1} />
                    <p className="font-display text-2xl mb-2">Drop your selfie here</p>
                    <p className="text-[#8B8494] text-sm">or click to browse · JPEG / PNG</p>
                  </div>
                )}
                <input ref={inputRef} data-testid="file-input" type="file" accept="image/*" hidden onChange={(e) => handleFile(e.target.files[0])} />
              </div>

              <label data-testid="consent-label" className="flex items-start gap-3 mt-6 cursor-pointer text-sm text-[#57515F]">
                <input data-testid="consent-checkbox" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-4 h-4 accent-[#9A7420]" />
                <span>I understand this is cosmetic guidance (not a medical diagnosis) and I consent to my photo being analyzed. My photo is <b>not stored</b>.</span>
              </label>
              <div className="flex gap-4 mt-4">
                <button data-testid="analyze-button" onClick={analyze} disabled={!base64 || loading || !consent} className="btn-primary flex-1 justify-center disabled:opacity-40">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ScanFace className="w-4 h-4" /> Analyze My Skin</>}
                </button>
                {preview && (
                  <button data-testid="reset-button" onClick={reset} className="btn-secondary px-5"><RefreshCw className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          </Reveal>

          <div>
            {!result && !loading && (
              <div className="border border-[#DAD5E2] rounded-2xl p-10 h-full flex items-center justify-center text-center bg-[#E4E0EB]">
                <p className="text-[#8B8494] max-w-xs">Your personalised 12-parameter breakdown will appear here.</p>
              </div>
            )}
            {loading && (
              <div className="border border-[#DAD5E2] rounded-2xl p-10 h-full flex flex-col items-center justify-center bg-[#E4E0EB]">
                <Loader2 className="w-10 h-10 animate-spin text-[#B0872A] mb-4" />
                <p className="text-[#57515F]">Reading your skin across 12 parameters…</p>
              </div>
            )}
            {result && result.overall_score > 0 && (
              <motion.div data-testid="analysis-result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="on-dark bg-[#2B2340] text-white rounded-2xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="overline text-[#B0872A] mb-1">Overall Skin Score</p>
                    <p className="font-display text-6xl leading-none">{result.overall_score}<span className="text-2xl text-white/40">/100</span></p>
                  </div>
                  <span className="bg-[#B0872A] text-[#1A1A1A] px-4 py-1.5 rounded-full text-sm">{result.skin_type}</span>
                </div>
                <p className="text-white/70 text-sm leading-relaxed mb-8">{result.summary}</p>

                <div className="space-y-3 mb-8 max-h-72 overflow-y-auto pr-2">
                  {result.parameters?.map((p) => (
                    <div key={p.name} data-testid={`param-${p.name}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-white/80">{p.name}</span>
                        <span className="text-[#B0872A]">{p.score}</span>
                      </div>
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${p.score}%` }} transition={{ duration: 0.9, ease: "easeOut" }} className="h-full bg-[#B0872A]" />
                      </div>
                    </div>
                  ))}
                </div>

                {result.recommended_treatments?.length > 0 && (
                  <div className="mb-6">
                    <p className="overline text-[#B0872A] mb-3">Recommended Treatments</p>
                    <ul className="space-y-2">
                      {result.recommended_treatments.map((t) => (
                        <li key={t.name} className="text-sm">
                          <span className="text-white font-medium">{t.name}</span>
                          <span className="text-white/50"> — {t.reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Link to="/consultation" data-testid="result-consultation-btn" className="btn-primary bg-[#B0872A] text-[#1A1A1A] w-full justify-center">
                  Book Free Consultation <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            )}
            {result && result.overall_score === 0 && (
              <div className="border border-[#DAD5E2] rounded-2xl p-10 h-full flex items-center justify-center text-center bg-[#E4E0EB]">
                <p className="text-[#57515F] max-w-xs">{result.summary || "Please upload a clearer, well-lit selfie of your face."}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
